import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { ApiKey } from '../../_shared/domain/api-key.model';
import type { ApiKeyDraft } from '../domain/api-key-draft.model';
import { validateDraft } from '../domain/api-key-draft.validation';
import { toEditApiKeyError } from '../domain/edit-api-key-error';
import type { EditApiKeyGateway } from '../infrastructure/edit-api-key.gateway';
import { loadRequested, submitRequested } from './edit-api-key.actions';
import { selectApiKey, selectDraft } from './edit-api-key.selectors';
import { editApiKeyActions } from './edit-api-key.slice';

/**
 * WORKER: hydrate the key being edited. Any load failure (not found / network)
 * becomes a business error so the page can redirect (mirrors the old page's
 * `.catch(() => navigate away)`). All I/O goes through the injected gateway.
 */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: EditApiKeyGateway = yield getContext('editApiKeyGateway');
  yield put(editApiKeyActions.loadStarted());
  try {
    const key = (yield call(gateway.loadApiKey, action.payload)) as ApiKey;
    yield put(editApiKeyActions.loadSucceeded(key));
  } catch (e) {
    yield put(editApiKeyActions.loadFailed(toEditApiKeyError(e)));
  }
}

/**
 * WORKER: persist the edited draft. Validates first (pure domain); on failure
 * dispatches a `validation` error and stops without touching the gateway.
 */
function* saveWorker() {
  const apiKey = (yield select(selectApiKey)) as ApiKey | null;
  if (!apiKey) return;
  const draft = (yield select(selectDraft)) as ApiKeyDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(editApiKeyActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: EditApiKeyGateway = yield getContext('editApiKeyGateway');
  yield put(editApiKeyActions.saveStarted());
  try {
    yield call(gateway.updateApiKey, apiKey.id, draft);
    yield put(editApiKeyActions.saveSucceeded());
  } catch (e) {
    yield put(editApiKeyActions.saveFailed(toEditApiKeyError(e)));
  }
}

/** WATCHERS. */
export function* editApiKeySaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(submitRequested.type, saveWorker),
  ]);
}
